import React, { useEffect } from "react";
import classNames from "classnames";
import { Query } from "@apollo/client/react/components";
import { useHistory } from "react-router-dom";
import { ApolloError } from "@apollo/client";

import { GetTestsCountDocument, GetTestsDocument, Tester } from "generated/graphql";
// @ts-ignore
import Headwayapp from "app/headwayapp";
import NavigationElement from "./components/navigation_element";
import NavigationBadge from "./components/navigation_badge/index";
import NavigationSection from "./components/navigation_section";
import Title from "./components/title";
import Logo from "./components/logo";
import ToggleIcon from "./components/toggle_icon";
import { testerAccountPaths } from "configuration/app_paths";
import styles from "./style.module.scss";

const SupportCentreLink = () => (
  <li className="hover:bg-body py-sm px-2xl">
    <a
      className="text-muted hover:text-muted block focus:text-muted focus:no-underline"
      href="https://gathelp.zendesk.com/hc/en-us"
      target="_blank"
    >
      Support centre
    </a>
  </li>
);

type SideNavigationProps = {
  currentTester: Tester;
  toggleSideBar: () => void;
  isSideBarCollapsed: boolean;
};

type TestsQueryProps = {
  loading: boolean;
  error?: ApolloError | undefined;
  data: {
    tests: {
      totalCount: number;
    };
    joinedTests: {
      totalCount: number;
    };
  };
};

type TestsCountQueryProps = {
  loading: boolean;
  error?: ApolloError | undefined;
  data: {
    reproducibilityTests: {
      totalCount: number;
    };
    joinedTests: {
      totalCount: number;
    };
  };
};

const SideNavigation = ({ currentTester, toggleSideBar, isSideBarCollapsed }: SideNavigationProps) => {
  const history = useHistory();

  useEffect(() => {
    const historyListener = history.listen(closeSidebar);

    return historyListener;
  });

  const closeSidebar = () => {
    if (!isSideBarCollapsed) {
      toggleSideBar();
    }
  };

  const renderAuthorizedLinks = () => {
    if (currentTester.profileCompletenessStage !== "finished") return [];

    return [renderTestsSection(), renderGrowthSection(), renderAccountSection(), renderSupportSection()];
  };

  const renderTestsSection = () => (
    <NavigationSection title="Tests" key="tests-section">
      {renderTestCyclesLink()}
      {renderReproducibilityTestsLink()}
      {renderAttachmentsNavigationLinks()}
    </NavigationSection>
  );

  const renderGrowthSection = () => (
    <NavigationSection title="Growth" key="growth-section">
      {renderPerformanceNavigationLink()}
      {renderWorkHistoryLink()}
      {renderPayoutsNavigationLinks()}
    </NavigationSection>
  );

  const renderAccountSection = () => (
    <NavigationSection title="Account" key="account-section">
      {renderProfileLink()}
      {renderDevicesLink()}
      {renderTestSettingsLink()}
      {renderAccountSettingsLink()}
    </NavigationSection>
  );

  const renderSupportSection = () => (
    <NavigationSection title="Support" key="support-section">
      <SupportCentreLink key="support-centre-link" />
      <li className="hover:bg-body py-sm px-2xl">
        <Headwayapp />
      </li>
    </NavigationSection>
  );

  const renderLogOutSection = () => <NavigationSection key="log-out-section">{renderLogOutLink()}</NavigationSection>;

  const renderTestCyclesLink = () => (
    <NavigationElement
      route={testerAccountPaths.testCycles}
      title="Test cycles"
      key="test-cycles-link"
      className="flex items-center justify-between"
    >
      <Query query={GetTestsDocument} fetchPolicy="cache-and-network">
        {({ loading, error, data }: TestsQueryProps) => {
          if (loading || error || !data) return null;

          return <NavigationBadge totalCount={data.tests.totalCount} muted={data.joinedTests.totalCount > 0} />;
        }}
      </Query>
    </NavigationElement>
  );

  const renderAttachmentsNavigationLinks = () => (
    <NavigationElement route={testerAccountPaths.attachments} title="Attachments" key="attachments-links" />
  );

  const renderPayoutsNavigationLinks = () => (
    <NavigationElement route={testerAccountPaths.payouts} title="Payouts" key="payouts-links" />
  );

  const renderReproducibilityTestsLink = () => (
    <NavigationElement
      route={testerAccountPaths.reproducibilityTests}
      title="Reproducibility tests"
      key="reproducibility-tests"
      className="flex items-center justify-between"
    >
      <Query query={GetTestsCountDocument} variables={{ isOpen: true }} fetchPolicy="cache-and-network">
        {({ loading, error, data }: TestsCountQueryProps) => {
          if (loading || error || !data) return null;

          return (
            <NavigationBadge
              totalCount={data.reproducibilityTests.totalCount}
              muted={data.joinedTests.totalCount > 0}
            />
          );
        }}
      </Query>
    </NavigationElement>
  );

  const renderPerformanceNavigationLink = () => (
    <NavigationElement route={testerAccountPaths.performance} title="Performance" key="performance-link" />
  );

  const renderWorkHistoryLink = () => (
    <NavigationElement route={testerAccountPaths.workHistory} title="Work history" key="work-history-link" />
  );

  const renderProfileLink = () => (
    <NavigationElement route={testerAccountPaths.profileSettings} title="Profile" key="profile-link" />
  );

  const renderDevicesLink = () => (
    <NavigationElement route={testerAccountPaths.profileHardwareAndSoftware} title="Devices" key="devices-link" />
  );

  const renderTestSettingsLink = () => (
    <NavigationElement
      route={testerAccountPaths.profileTestingSettings}
      title="Test settings"
      key="test-settings-link"
    />
  );

  const renderAccountSettingsLink = () => (
    <NavigationElement route={testerAccountPaths.accountTestingSettings} title="Settings" key="account-settings-link" />
  );

  const renderLogOutLink = () => (
    <NavigationElement route={testerAccountPaths.signOut} title="Log out" key="log-out-link" />
  );

  const { firstName, testerRank } = currentTester;

  return (
    <div className="bg-darker z-20 lg:z-10">
      <nav
        className={classNames(
          "navbar-static-side",
          {
            "nav-is-visible": !isSideBarCollapsed,
          },
          "side-navigation",
          "bg-darker",
          "fixed top-0 bottom-0 overflow-y-auto h-full",
        )}
        role="navigation"
        data-test-role="navigation"
        data-testid="side-navigation"
        style={{ width: "240px" }}
      >
        <ToggleIcon toggleSideBar={toggleSideBar} />
        <Logo />
        <Title firstName={firstName} testerRank={testerRank} />
        {renderAuthorizedLinks()}
        {renderLogOutSection()}
      </nav>
      {!isSideBarCollapsed && <div className={`overlay fade-in ${styles["sidebar-navigation-overlay"]}`} />}
    </div>
  );
};

export default SideNavigation;
